import React from "react"
import type { SubmitHandler } from "react-hook-form"
import { useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Link, useLocation, useNavigate } from "react-router"

import { PageCard } from "@/components/page-card/page-card"
import { PageTitle } from "@/components/page-title"
import type { Organization } from "@/models/organization"
import {
  BillEditableFields,
  type EditBillSchema,
  billToFormValues,
  buildBillUpdates,
  editBillSchema,
  emptyFormValues,
  getSubmitErrorKey,
  hasBillFieldsChanged,
  toBillFieldSnapshot,
  useBillLockToggle,
  useConfirmDeleteBill,
} from "@/modules/admin/bills"
import { focusFirstInvalidField } from "@/nzds/forms/focus-first-invalid-field/focus-first-invalid-field"
import { Form } from "@/nzds/forms/form/form"
import { ResetButton } from "@/nzds/forms/reset-button/reset-button"
import { SubmitButton } from "@/nzds/forms/submit-button/submit-button"
import { useForm } from "@/nzds/forms/use-form/use-form"
import { useUpdateBill } from "@/services/bill/use-update-bill"
import type { Bill } from "@/services/core-api/types"
import { useUrl } from "@/utils/url"

import { Link as MuiLink } from "@mui/material"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Collapse from "@mui/material/Collapse"
import Stack from "@mui/material/Stack"

interface AdminBillEditFormProps {
  bill: Bill
  billId: number
  billIsFetching: boolean
  canEditBillFields: boolean
  organizationId: Organization["id"]
}

export const AdminBillEditForm: React.FC<AdminBillEditFormProps> = ({
  bill,
  billId,
  billIsFetching,
  canEditBillFields,
  organizationId,
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation("common")
  const { adminBillPath, adminBillsPath } = useUrl()
  const [submitErrorMessage, setSubmitErrorMessage] = React.useState<
    string | null
  >(null)

  const formDefaultValues = billToFormValues(bill)
  const form = useForm({
    schema: editBillSchema,
    defaultValues: formDefaultValues,
  })

  const { updateBillMutateAsync, updateBillIsPending } = useUpdateBill()
  const {
    clearLockToggleUiValue,
    displayedLockedValue,
    handleLockChange,
    isLockToggleSyncing,
  } = useBillLockToggle({
    bill,
    organizationId,
    updateBillMutateAsync,
  })

  const watchedFields = useWatch<EditBillSchema>({
    control: form.control,
    defaultValue: formDefaultValues,
  })
  const watchedChangeReason = watchedFields.changeReason ?? ""
  const billFieldSnapshot = toBillFieldSnapshot(bill)
  const hasBillFieldChanges = hasBillFieldsChanged(
    { ...emptyFormValues, ...watchedFields },
    billFieldSnapshot
  )
  const isBusy = updateBillIsPending || billIsFetching

  const { confirmAndDeleteBill, deleteBillIsPending } = useConfirmDeleteBill({
    organizationId,
    onSuccess: () => {
      void navigate(adminBillsPath, { replace: true })
    },
  })

  React.useEffect(() => {
    if ((watchedChangeReason ?? "").trim() !== "") {
      form.clearErrors("changeReason")
    }
  }, [watchedChangeReason, form])

  const navigateToShow = (options?: { showDataFreshness?: boolean }) => {
    void navigate(`${adminBillPath(String(billId))}${location.search}`, {
      replace: true,
      state: options?.showDataFreshness
        ? { showDataFreshness: true }
        : undefined,
    })
  }

  const handleSubmit: SubmitHandler<EditBillSchema> = async (values) => {
    if (!hasBillFieldChanges) {
      setSubmitErrorMessage(t("editBill.noFieldChangesError"))
      return
    }

    setSubmitErrorMessage(null)

    if (values.changeReason.trim() === "") {
      const changeReasonError = {
        message: t("validation.required"),
        type: "manual" as const,
      }
      form.setError("changeReason", changeReasonError)
      focusFirstInvalidField({
        errors: { changeReason: changeReasonError },
        setFocus: form.setFocus,
      })
      return
    }

    const updates = buildBillUpdates(values, bill)

    try {
      await updateBillMutateAsync({
        billId: bill.id,
        organizationId,
        updates,
      })
      navigateToShow({ showDataFreshness: true })
    } catch (error: unknown) {
      setSubmitErrorMessage(t(getSubmitErrorKey(error)))
    }
  }

  const handleCancel = () => {
    if (!updateBillIsPending && !deleteBillIsPending) {
      clearLockToggleUiValue()
      setSubmitErrorMessage(null)
      navigateToShow()
    }
  }

  const handleDeleteClick = () => {
    if (displayedLockedValue) {
      return
    }

    confirmAndDeleteBill(bill.id).catch(() => undefined)
  }

  return (
    <Form<EditBillSchema>
      aria-label={t("editBill.title")}
      form={form}
      onSubmit={handleSubmit}
    >
      <Stack sx={{ gap: 2 }}>
        <PageTitle
          breadcrumbActions={
            <>
              <MuiLink
                component={Link}
                to={`${adminBillsPath}${location.search}`}
                underline="hover"
              >
                {t("admin.bills")}
              </MuiLink>
              <MuiLink
                component={Link}
                to={`${adminBillPath(String(billId))}${location.search}`}
                underline="hover"
              >
                {t("admin.billDetail.viewBill")}
              </MuiLink>
            </>
          }
          heading={t("editBill.title")}
          primaryActions={
            <>
              <ResetButton
                disabled={updateBillIsPending || deleteBillIsPending}
                onClick={handleCancel}
                variant="outlined"
              >
                {t("button.cancel")}
              </ResetButton>
              <SubmitButton
                disabled={
                  !canEditBillFields ||
                  updateBillIsPending ||
                  deleteBillIsPending ||
                  billIsFetching ||
                  displayedLockedValue ||
                  !hasBillFieldChanges
                }
              >
                {t("button.save")}
              </SubmitButton>
            </>
          }
          secondaryActions={
            <>
              <Button
                disabled={
                  !canEditBillFields ||
                  billIsFetching ||
                  updateBillIsPending ||
                  isLockToggleSyncing
                }
                onClick={() => {
                  void handleLockChange(!displayedLockedValue)
                }}
              >
                {displayedLockedValue
                  ? t("editBill.unlockAction")
                  : t("editBill.lockAction")}
              </Button>
              <Button
                color="error"
                disabled={
                  !canEditBillFields ||
                  updateBillIsPending ||
                  deleteBillIsPending ||
                  billIsFetching ||
                  displayedLockedValue
                }
                onClick={handleDeleteClick}
              >
                {t("button.delete")}
              </Button>
            </>
          }
        />
        <PageCard>
          <Stack sx={{ gap: 2, pb: 1 }}>
            <Collapse in={displayedLockedValue} unmountOnExit>
              <Alert severity="info">
                {t("editBill.lockedReadOnlyNotice")}
              </Alert>
            </Collapse>
            <Collapse in={Boolean(submitErrorMessage)} unmountOnExit>
              <Alert severity="error">{submitErrorMessage}</Alert>
            </Collapse>
            <BillEditableFields
              bill={bill}
              canEditBillFields={canEditBillFields}
              displayedLockedValue={displayedLockedValue}
              hasBillFieldChanges={hasBillFieldChanges}
              isBusy={isBusy}
            />
          </Stack>
        </PageCard>
      </Stack>
    </Form>
  )
}
